// src/components/ReminderActionMenu.jsx
// ============================================================================
// Contextual action menu for invoice communication workflows.
// Shows appropriate actions based on invoice lifecycle status.
// Not a full email client — opens drafts in the user's external mail app.
// ============================================================================
import React, { useState, useRef, useEffect } from 'react';
import { Mail, Bell, AlertTriangle, MoreHorizontal } from 'lucide-react';

// ── Action definitions ───────────────────────────────────────────────────────
const ACTIONS = {
  draft_invoice: {
    key: 'invoice_sent',
    label: 'Draft Invoice Email',
    icon: Mail,
    color: 'var(--brio-gold, #9F902A)',
    description: 'Open invoice email draft in mail app',
  },
  reminder_friendly: {
    key: 'reminder_friendly',
    label: 'Friendly Reminder',
    icon: Bell,
    color: '#f59e0b',
    description: 'Gentle payment reminder',
  },
  reminder_due_today: {
    key: 'reminder_due_today',
    label: 'Due Today',
    icon: Bell,
    color: '#f97316',
    description: 'Payment due today notice',
  },
  reminder_overdue: {
    key: 'reminder_overdue',
    label: 'Overdue Reminder',
    icon: Bell,
    color: '#ef4444',
    description: 'Overdue payment reminder',
  },
  reminder_final: {
    key: 'reminder_final',
    label: 'Final Reminder',
    icon: Bell,
    color: '#dc2626',
    description: 'Final payment notice',
  },
};

/**
 * Derive which actions are appropriate based on invoice status.
 * @param {string} displayStatus - 'draft' | 'sent' | 'due' | 'overdue' | 'paid'
 * @param {number} [daysOverdue] - Number of days past due date
 * @returns {string[]} - Array of action keys to show
 */
function getAvailableActions(displayStatus, daysOverdue = 0) {
  switch (displayStatus) {
    case 'draft':
      return ['draft_invoice'];
    case 'sent':
      return ['reminder_friendly'];
    case 'due':
      return ['reminder_due_today', 'reminder_friendly'];
    case 'overdue':
      if (daysOverdue > 30) {
        return ['reminder_final', 'reminder_overdue', 'reminder_friendly'];
      }
      return ['reminder_overdue', 'reminder_friendly'];
    case 'paid':
      return []; // No actions for paid invoices
    default:
      return ['draft_invoice'];
  }
}

/**
 * ReminderActionMenu
 *
 * Contextual dropdown for invoice email/reminder actions.
 * Shows only appropriate actions based on invoice lifecycle.
 *
 * @param {object} props
 * @param {object}   props.invoice        - Invoice record
 * @param {string}   props.displayStatus  - Current display status
 * @param {number}   [props.daysOverdue]  - Days past due
 * @param {Function} props.onAction       - Callback with (invoice, templateKey)
 * @param {boolean}  [props.compact]      - Compact mode (icon only trigger)
 * @param {boolean}  [props.disabled]     - Disable the menu
 */
export default function ReminderActionMenu({
  invoice,
  displayStatus,
  daysOverdue = 0,
  onAction,
  compact = false,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Click-outside handler
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const actions = getAvailableActions(displayStatus, daysOverdue);
  if (actions.length === 0) return null;

  // Single action: render inline button (no dropdown needed)
  if (actions.length === 1 && !compact) {
    const action = ACTIONS[actions[0]];
    if (!action) return null;
    const Icon = action.icon;
    return (
      <button
        onClick={() => onAction(invoice, action.key)}
        disabled={disabled}
        className="brio-btn brio-btn-secondary"
        style={{
          padding: '2px 8px', fontSize: '10px',
          display: 'inline-flex', alignItems: 'center', gap: '4px',
          opacity: disabled ? 0.5 : 1,
        }}
        title={action.description}
      >
        <Icon className="w-3 h-3" style={{ color: action.color }} />
        {action.label}
      </button>
    );
  }

  // Multiple actions: dropdown menu
  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen(!open)}
        disabled={disabled}
        className="brio-btn brio-btn-secondary"
        style={{
          padding: compact ? '2px 4px' : '2px 8px',
          fontSize: '10px',
          display: 'inline-flex', alignItems: 'center', gap: '4px',
          opacity: disabled ? 0.5 : 1,
        }}
        title="Email actions"
      >
        <Mail className="w-3 h-3" style={{ color: 'var(--brio-gold, #9F902A)' }} />
        {!compact && 'Email'}
        <MoreHorizontal className="w-3 h-3" style={{ color: '#94a3b8' }} />
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, zIndex: 9999,
          marginTop: '4px', minWidth: '200px',
          background: '#fff',
          border: '1px solid var(--brio-border, #d1d5db)',
          borderRadius: '6px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{
            padding: '6px 10px', fontSize: '10px', fontWeight: 600,
            color: 'var(--brio-muted, #94a3b8)',
            borderBottom: '1px solid var(--brio-border, #d1d5db)',
            textTransform: 'uppercase', letterSpacing: '0.05em',
          }}>
            Communication
          </div>

          {actions.map(actionKey => {
            const action = ACTIONS[actionKey];
            if (!action) return null;
            const Icon = action.icon;
            return (
              <button
                key={actionKey}
                onClick={() => { onAction(invoice, action.key); setOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  width: '100%', padding: '7px 10px',
                  background: 'none', border: 'none',
                  cursor: 'pointer', textAlign: 'left',
                  fontSize: '11px', color: 'var(--brio-text, #475569)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
              >
                <Icon className="w-3 h-3" style={{ color: action.color, flexShrink: 0 }} />
                <div>
                  <div style={{ fontWeight: 500 }}>{action.label}</div>
                  <div style={{ fontSize: '9px', color: 'var(--brio-muted, #94a3b8)' }}>{action.description}</div>
                </div>
              </button>
            );
          })}

          {/* Disclaimer */}
          <div style={{
            padding: '5px 10px', fontSize: '9px',
            color: 'var(--brio-muted, #94a3b8)',
            borderTop: '1px solid var(--brio-border, #d1d5db)',
            fontStyle: 'italic',
          }}>
            Opens draft in your mail app
          </div>
        </div>
      )}
    </div>
  );
}

// Export action derivation for use in other components
export { getAvailableActions };
